import { useId, useRef, useState } from 'react';
import { Check, Eye, FileText, Pencil } from 'lucide-react';
import {
  REPLY_MAX_LENGTH,
  resolveReplyTemplate,
  type ReplyTemplate,
  type ReplyVariable,
} from '@/lib/replyTemplates';
import { Button } from './Button';
import { Input } from './Input';
import { MessageContent } from './MessageContent';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Textarea } from './Textarea';

export function ReplyPreview({ text }: { text: string }) {
  const id = useId();
  return (
    <section
      data-slot="reply-preview"
      aria-labelledby={id}
      className="min-w-0 rounded-md border border-line bg-muted p-4"
    >
      <h4 id={id} className="mb-3 flex items-center gap-2 type-overline text-fg-muted">
        <Eye className="size-3.5" aria-hidden />
        Предпросмотр ответа
      </h4>
      <div className="max-h-64 overflow-y-auto">
        <MessageContent content={{ format: 'plain', text }} />
      </div>
    </section>
  );
}
interface ReplyTemplatesProps {
  templates: readonly ReplyTemplate[];
  variables: readonly ReplyVariable[];
  onVariableChange: (key: string, value: string) => void;
  onApply: (text: string) => void;
  replacing?: boolean;
}
export function ReplyTemplates({
  templates,
  variables,
  onVariableChange,
  onApply,
  replacing = false,
}: ReplyTemplatesProps) {
  const [selected, setSelected] = useState('none');
  const labelId = useId();
  const template = templates.find((item) => item.id === selected);
  const resolved = template ? resolveReplyTemplate(template.body, variables) : null;
  return (
    <section data-slot="reply-templates" className="min-w-0 space-y-3">
      <label id={labelId} className="flex items-center gap-2 text-xs font-medium text-fg-muted">
        <FileText className="size-3.5" aria-hidden />
        Шаблон ответа
      </label>
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger aria-labelledby={labelId}>
          <SelectValue placeholder="Выбрать шаблон" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Без шаблона</SelectItem>
          {templates.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {item.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {resolved && (
        <div className="space-y-4 rounded-md border border-line bg-card p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {variables
              .filter((variable) => resolved.used.includes(variable.key))
              .map((variable) => (
                <Input
                  key={variable.key}
                  label={variable.label}
                  value={variable.value ?? ''}
                  maxLength={2000}
                  errorTone="strong"
                  onChange={(event) => onVariableChange(variable.key, event.target.value)}
                  error={
                    resolved.missing.includes(variable.key) ? 'Заполни поле для подстановки.' : undefined
                  }
                />
              ))}
          </div>
          <ReplyPreview text={resolved.text.slice(0, REPLY_MAX_LENGTH)} />
          {!resolved.ready && (
            <p role="status" className="text-xs text-danger-strong">
              {resolved.tooLong
                ? 'Ответ слишком длинный: не больше 8000 символов.'
                : resolved.malformed
                  ? 'В шаблоне непарные скобки переменной.'
                  : 'Не заполнены переменные: ' +
                    resolved.missing
                      .map((key) => variables.find((item) => item.key === key)?.label ?? key)
                      .join(', ')}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              disabled={!resolved.ready}
              onClick={() => onApply(resolved.text)}
            >
              <Check className="size-3.5" aria-hidden />
              {replacing ? 'Заменить текст ответа' : 'Вставить в ответ'}
            </Button>
            <span className="text-xs text-fg-muted">После вставки текст можно изменить.</span>
          </div>
        </div>
      )}
    </section>
  );
}
export function ReplyComposer({
  value,
  onValueChange,
  templates,
  variables,
  onVariableChange,
  onSubmit,
  submitLabel = 'Отправить ответ',
}: Omit<ReplyTemplatesProps, 'onApply' | 'replacing'> & {
  value: string;
  onValueChange: (value: string) => void;
  onSubmit: (text: string) => void;
  submitLabel?: string;
}) {
  const field = useRef<HTMLTextAreaElement>(null);
  const [review, setReview] = useState<string | null>(null);
  const text = value.trim();
  const reviewing = review === text && !!text;
  const valid = !!text && value.length <= REPLY_MAX_LENGTH;
  return (
    <div data-slot="reply-composer" className="min-w-0 space-y-4">
      <ReplyTemplates
        templates={templates}
        variables={variables}
        onVariableChange={onVariableChange}
        replacing={!!text}
        onApply={(next) => {
          onValueChange(next);
          setReview(null);
          field.current?.focus();
        }}
      />
      <Textarea
        ref={field}
        label="Ответ"
        rows={4}
        maxLength={REPLY_MAX_LENGTH}
        value={value}
        onChange={(event) => {
          onValueChange(event.target.value);
          setReview(null);
        }}
        helper="Перед отправкой проверь окончательный текст."
      />
      {reviewing && <ReplyPreview text={text} />}
      <div className="flex flex-wrap justify-end gap-3">
        {reviewing && (
          <Button
            variant="ghost"
            onClick={() => {
              setReview(null);
              field.current?.focus();
            }}
          >
            <Pencil className="size-4" aria-hidden />
            Править ответ
          </Button>
        )}
        {reviewing ? (
          <Button
            disabled={!valid}
            onClick={() => {
              if (valid) onSubmit(text);
            }}
          >
            {submitLabel}
          </Button>
        ) : (
          <Button variant="secondary" disabled={!valid} onClick={() => setReview(text)}>
            <Eye className="size-4" aria-hidden />
            Предпросмотр
          </Button>
        )}
      </div>
    </div>
  );
}
